/* ═══════════════════════════════════════════════════
   FAQ ENTRIES — single source of truth
   Used by /faq (master page) and /faq/:slug (article pages)
   Designed for AEO: direct, executive-friendly answers
   ═══════════════════════════════════════════════════ */

export const FAQ_ENTRIES = [
  {
    slug: "how-can-ai-improve-my-business-operations",
    question: "How can AI improve my business operations?",
    short:
      "AI improves business operations by reducing repetitive work, improving decision-making, increasing visibility into workflows, and helping teams execute faster.",
    answer:
      "AI improves business operations by reducing repetitive work, improving decision-making, increasing visibility into workflows, and helping teams execute faster. At 589 Labs, we identify the specific operational workflows where AI can create measurable impact — then build a practical roadmap to implement and measure results.",
    icon: "Workflow",
    keywords: ["AI operations", "business automation", "operational efficiency"],
  },
  {
    slug: "best-ai-use-cases-for-ceos",
    question: "What are the best AI use cases for CEOs?",
    short:
      "The best AI use cases for CEOs are operational, not experimental — customer service, lead generation, reporting, workflow automation, knowledge management, forecasting, and internal communication.",
    answer:
      "The best AI use cases for CEOs are typically operational, not experimental. High-impact areas often include customer service, lead generation, reporting, workflow automation, knowledge management, forecasting, and internal communication. 589 Labs helps leadership teams prioritize the top 3 AI opportunities with the highest ROI potential.",
    icon: "Target",
    keywords: ["AI for CEOs", "AI use cases", "executive AI"],
  },
  {
    slug: "where-to-start-with-ai",
    question: "How do I know where to start with AI?",
    short:
      "Start with strategy, not tools. An industry-specific AI assessment identifies operational pain points, inefficiencies, and the opportunities where AI delivers measurable value quickly.",
    answer:
      "Most organizations struggle because they start with tools instead of strategy. The best starting point is an industry-specific AI assessment that identifies operational pain points, inefficiencies, and opportunities where AI can deliver measurable business value quickly.",
    icon: "Compass",
    keywords: ["where to start with AI", "AI strategy", "AI assessment"],
  },
  {
    slug: "what-is-an-ai-agent",
    question: "What is an AI agent?",
    short:
      "An AI agent is software that performs tasks autonomously using artificial intelligence — taking actions, following workflows, and completing multi-step processes with limited human intervention.",
    answer:
      "An AI agent is software that can perform tasks autonomously using artificial intelligence. Unlike a chatbot, an AI agent can take actions, follow workflows, retrieve information, communicate with systems, and complete multi-step processes with limited human intervention.",
    icon: "Bot",
    keywords: ["AI agent", "autonomous AI", "agentic AI"],
  },
  {
    slug: "how-can-ai-agents-help-my-company",
    question: "How can AI agents help my company?",
    short:
      "AI agents automate repetitive workflows, support customer interactions, summarize information, generate reports, monitor systems, and help teams execute faster — tied directly to KPIs.",
    answer:
      "AI agents can automate repetitive workflows, support customer interactions, summarize information, generate reports, monitor systems, and help teams execute faster. The most successful implementations focus on practical business workflows tied directly to KPIs and operational outcomes.",
    icon: "Zap",
    keywords: ["AI agents for business", "agent automation", "workflow agents"],
  },
  {
    slug: "departments-that-benefit-most-from-ai",
    question: "What departments benefit most from AI?",
    short:
      "Operations, sales, customer service, HR, finance, marketing, and executive leadership benefit most — wherever there's repetitive work, slow response times, manual reporting, or disconnected systems.",
    answer:
      "Departments that benefit most from AI often include operations, sales, customer service, HR, finance, marketing, and executive leadership. The best opportunities are usually areas with repetitive work, slow response times, manual reporting, or disconnected systems.",
    icon: "Building2",
    keywords: ["AI departments", "AI by function", "AI for sales operations"],
  },
  {
    slug: "how-much-does-ai-implementation-cost",
    question: "How much does AI implementation cost?",
    short:
      "AI implementation cost depends on scope, systems, and complexity. Most organizations begin with a focused assessment and roadmap before expanding into larger phased initiatives.",
    answer:
      "AI implementation costs vary depending on scope, systems, and complexity. Many organizations begin with a focused assessment and implementation roadmap before expanding into larger initiatives. 589 Labs focuses on practical, phased implementations designed to deliver measurable ROI quickly.",
    icon: "DollarSign",
    keywords: ["AI implementation cost", "AI pricing", "AI investment"],
  },
  {
    slug: "how-long-does-it-take-to-implement-ai",
    question: "How long does it take to implement AI?",
    short:
      "Simple AI workflows can be live in weeks; broader operational transformations take several months. The fastest wins come from focusing on 3 high-impact workflows first.",
    answer:
      "Simple AI workflows can often be implemented within weeks, while broader operational transformations may take several months. The fastest successes usually come from focusing on 3 high-impact workflows first rather than trying to transform the entire organization at once.",
    icon: "Clock",
    keywords: ["AI implementation timeline", "AI rollout", "AI deployment"],
  },
  {
    slug: "what-roi-should-i-expect-from-ai",
    question: "What ROI should I expect from AI?",
    short:
      "AI ROI is measured in time savings, operational efficiency, revenue growth, improved customer experience, reduced manual work, and faster decision-making — every implementation should track KPIs from day one.",
    answer:
      "AI ROI is typically measured through time savings, operational efficiency, revenue growth, improved customer experience, reduced manual work, and faster decision-making. At 589 Labs, every implementation includes KPI tracking to ensure AI initiatives are tied directly to business outcomes.",
    icon: "TrendingUp",
    keywords: ["AI ROI", "AI business value", "AI KPIs"],
  },
  {
    slug: "risks-of-ai-for-businesses",
    question: "What are the risks of AI for businesses?",
    short:
      "The biggest risks are poor implementation, weak governance, disconnected tools, low adoption, and unclear business objectives. Strong AI programs require ownership, measurable goals, and realistic workflows.",
    answer:
      "The biggest risks are poor implementation, lack of governance, disconnected tools, weak adoption, and unclear business objectives. Successful AI programs require clear ownership, measurable goals, and practical workflows that teams can realistically adopt.",
    icon: "ShieldAlert",
    keywords: ["AI risks", "AI governance", "AI adoption"],
  },
  {
    slug: "do-small-businesses-need-ai",
    question: "Do small businesses need AI?",
    short:
      "Small businesses may benefit even more from AI than large ones — lean teams gain disproportionate leverage from automation, consistency, productivity, and scaling without adding overhead.",
    answer:
      "Small businesses may benefit even more from AI because they often operate with lean teams and limited resources. AI can help smaller organizations automate work, improve consistency, increase productivity, and scale without adding unnecessary overhead.",
    icon: "Sparkles",
    keywords: ["AI for small business", "AI for SMB", "small business AI"],
  },
  {
    slug: "how-do-i-create-an-ai-strategy",
    question: "How do I create an AI strategy?",
    short:
      "An effective AI strategy identifies where AI creates measurable business value — assess workflows, prioritize opportunities, define KPIs, assign ownership, and build a phased execution plan.",
    answer:
      "An effective AI strategy starts by identifying where AI can create measurable business value. That includes assessing workflows, prioritizing opportunities, defining KPIs, assigning ownership, and building a phased execution plan tied to operational goals.",
    icon: "Map",
    keywords: ["AI strategy", "build AI strategy", "AI roadmap"],
  },
  {
    slug: "what-is-a-chief-ai-officer",
    question: "What is a Chief AI Officer?",
    short:
      "A Chief AI Officer (CAIO) guides AI strategy, governance, implementation, and business adoption. Many companies now use fractional or outsourced CAIO services for executive AI leadership.",
    answer:
      "A Chief AI Officer (CAIO) is responsible for guiding AI strategy, governance, implementation, and business adoption across the organization. Many companies now use fractional or outsourced CAIO services to gain executive AI leadership without hiring a full-time executive.",
    icon: "Crown",
    keywords: ["Chief AI Officer", "CAIO", "fractional CAIO"],
  },
  {
    slug: "how-to-prepare-team-for-ai",
    question: "How do I prepare my team for AI?",
    short:
      "Preparing teams for AI requires communication, training, practical workflows, and clear leadership support — the goal is not replacement, it's helping teams work faster and focus on higher-value work.",
    answer:
      "Preparing teams for AI requires communication, training, practical workflows, and clear leadership support. The goal is not replacing employees — it is helping teams work faster, make better decisions, and focus on higher-value work.",
    icon: "Users",
    keywords: ["AI team readiness", "AI training", "AI adoption"],
  },
  {
    slug: "what-does-successful-ai-transformation-look-like",
    question: "What does a successful AI transformation look like?",
    short:
      "Successful AI transformations are measured by business outcomes, not hype — AI becomes part of daily operations, workflows are faster, teams adopt the tools, and leadership can measure operational improvements over time.",
    answer:
      "Successful AI transformations are measured by business outcomes, not hype. Organizations succeed when AI becomes part of daily operations, workflows become faster and more efficient, teams adopt the tools, and leadership can clearly measure operational improvements over time.",
    icon: "Award",
    keywords: ["AI transformation", "successful AI", "AI program success"],
  },
];

/* Convert FAQ_ENTRIES → legacy {q, a} shape for any consumer
   that expects the old DEFAULT_SITE_COPY.faq structure. */
export const FAQ_AS_QA = FAQ_ENTRIES.map((e) => ({ q: e.question, a: e.answer }));

/* Find related entries for the "Related questions" cross-link block. */
export function relatedFaqs(slug, n = 3) {
  const all = FAQ_ENTRIES.filter((e) => e.slug !== slug);
  // simple round-robin by index proximity to keep things deterministic
  const idx = FAQ_ENTRIES.findIndex((e) => e.slug === slug);
  const out = [];
  for (let i = 1; i <= all.length && out.length < n; i++) {
    const next = FAQ_ENTRIES[(idx + i) % FAQ_ENTRIES.length];
    if (next.slug !== slug) out.push(next);
  }
  return out;
}
